import React, { useCallback, useEffect, useState } from "react";
import {
  getSocialReels,
  refreshSocialReels,
  searchSocialReelListings,
  updateSocialReelListingMatch,
} from "./api";
import "./admin-reel-matches.css";

const platformLabel = (platform) =>
  platform === "instagram" ? "Instagram" : "Facebook";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "Fiyat bilgisi yok";
  if (/tl|₺/i.test(String(value))) return String(value);
  const number = Number(String(value).replaceAll(".", "").replace(",", "."));
  return Number.isFinite(number)
    ? `${new Intl.NumberFormat("tr-TR").format(number)} TL`
    : `${value} TL`;
};

const detectedCode = (caption = "") => {
  const match = String(caption).match(/\bBRN[\s-]*(\d{1,10})\b/i);
  return match ? `BRN-${match[1]}` : "Kod yok";
};

export function AdminReelMatchesPanel({ onError, onNotice }) {
  const [filters, setFilters] = useState({ platform: "", match: "all" });
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [listingSearch, setListingSearch] = useState("");
  const [listings, setListings] = useState([]);
  const [listingLoading, setListingLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (showLoader = true) => {
      if (showLoader) setLoading(true);
      try {
        const result = await getSocialReels(filters);
        setReels(result.reels || []);
      } catch (reason) {
        onError(reason.message);
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [filters, onError],
  );

  const synchronize = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await refreshSocialReels();
      const refresh = result.refresh || {};
      const count = Number(refresh.instagram || 0) + Number(refresh.facebook || 0);
      const auto = Number(refresh.auto_matched || 0);
      onNotice(`${count} Reels kontrol edildi${auto ? `, ${auto} ilan kodu otomatik eşleştirildi` : ""}.`);
      if (refresh.errors?.length) onNotice(`${refresh.errors.join(" ")} ${count} video güncellendi.`);
      await load();
    } catch (reason) {
      onError(reason.message);
    } finally {
      setSyncing(false);
    }
  }, [load, onError, onNotice]);

  useEffect(() => {
    synchronize();
  }, []); // İlk açılışta katalog ve BRN eşleşmeleri güncellenir.

  useEffect(() => {
    if (!selected) return;
    setListingSearch("");
    setListingLoading(true);
    searchSocialReelListings()
      .then((result) => setListings(result.listings || []))
      .catch((reason) => onError(reason.message))
      .finally(() => setListingLoading(false));
  }, [selected, onError]);

  const findListings = async (event) => {
    event?.preventDefault();
    setListingLoading(true);
    try {
      const result = await searchSocialReelListings(listingSearch);
      setListings(result.listings || []);
    } catch (reason) {
      onError(reason.message);
    } finally {
      setListingLoading(false);
    }
  };

  const saveMatch = async (listingId) => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateSocialReelListingMatch(selected, listingId);
      onNotice(listingId ? "Reels videosu seçilen ilana bağlandı." : "Reels–ilan eşleşmesi kaldırıldı.");
      setSelected(null);
      await load(false);
    } catch (reason) {
      onError(reason.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="reel-match-panel">
      <header className="reel-match-heading">
        <div>
          <p className="eyebrow">SOSYAL VİDEO YÖNETİMİ</p>
          <h2>Reels – ilan eşleştirme</h2>
          <p>Videonun açıklamasındaki BRN kodu doğru ilanla otomatik bağlanır. Yönetici eşleşmeyi her zaman değiştirebilir.</p>
        </div>
        <button className="reel-sync" disabled={syncing} onClick={synchronize}>
          {syncing ? "Videolar güncelleniyor…" : "Videoları ve kodları yenile"}
        </button>
      </header>

      <div className="reel-match-filters">
        <select
          aria-label="Platform"
          value={filters.platform}
          onChange={(event) => setFilters((old) => ({ ...old, platform: event.target.value }))}
        >
          <option value="">Tüm platformlar</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
        <select
          aria-label="Eşleşme durumu"
          value={filters.match}
          onChange={(event) => setFilters((old) => ({ ...old, match: event.target.value }))}
        >
          <option value="all">Tüm videolar</option>
          <option value="matched">Eşleşenler</option>
          <option value="unmatched">Eşleşmeyenler</option>
        </select>
        <button disabled={loading || syncing} onClick={() => load()}>
          Filtrele
        </button>
      </div>

      {loading ? (
        <p className="loading">Reels videoları yükleniyor…</p>
      ) : reels.length ? (
        <div className="reel-match-grid">
          {reels.map((reel) => {
            const matched = Boolean(reel.listing_id);
            return (
              <article className="reel-match-card" key={`${reel.platform}-${reel.account_id}-${reel.media_id}`}>
                <div className="reel-match-image">
                  {reel.thumbnail_url ? (
                    <img src={reel.thumbnail_url} alt="Reels küçük görseli" />
                  ) : (
                    <div className="reel-match-placeholder">Reels</div>
                  )}
                  <span className={`reel-platform ${reel.platform}`}>{platformLabel(reel.platform)}</span>
                </div>
                <div className="reel-match-card-content">
                  <h3>{reel.title || "Sosyal medya videosu"}</h3>
                  <p className="reel-caption">{reel.caption || "Açıklama yok"}</p>
                  <dl>
                    <div><dt>Algılanan kod</dt><dd>{detectedCode(reel.caption)}</dd></div>
                    <div><dt>Eşleşme</dt><dd className={matched ? "matched" : "unmatched"}>{matched ? reel.listing_code || reel.listing_id : "Eşleşmedi"}</dd></div>
                  </dl>
                  {matched ? (
                    <div className="reel-listing-summary">
                      <strong>{reel.listing_title || "İlan başlığı yok"}</strong>
                      <span>{money(reel.listing_price)}</span>
                      {reel.listing_url && <a href={reel.listing_url} target="_blank" rel="noreferrer">İlanı aç</a>}
                    </div>
                  ) : (
                    <p className="reel-no-listing">Bu video için bağlı ilan yok.</p>
                  )}
                  <div className="reel-match-actions">
                    {reel.permalink && <a href={reel.permalink} target="_blank" rel="noreferrer">Videoyu aç</a>}
                    <button onClick={() => setSelected(reel)}>{matched ? "Eşleşmeyi değiştir" : "İlan seç"}</button>
                    {matched && <button className="plain-danger" onClick={() => saveMatch("")}>Eşleşmeyi kaldır</button>}
                  </div>
                  <small>{reel.mapping_source === "manual" ? "Elle düzenlendi" : matched ? "BRN koduyla otomatik eşleşti" : "BRN kodu bulunamadı"}</small>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="reel-match-empty">
          <strong>Gösterilecek Reels videosu yok.</strong>
          <p>Instagram/Facebook hesap bağlantılarını ve erişimlerini kontrol ettikten sonra videoları yenileyin.</p>
        </div>
      )}

      {selected && (
        <div className="reel-match-dialog-backdrop" role="presentation" onMouseDown={() => !saving && setSelected(null)}>
          <section className="reel-match-dialog" role="dialog" aria-modal="true" aria-label="İlan seç" onMouseDown={(event) => event.stopPropagation()}>
            <button className="dialog-close" aria-label="Kapat" onClick={() => setSelected(null)} disabled={saving}>×</button>
            <p className="eyebrow">ELLE EŞLEŞTİR</p>
            <h2>{selected.title || "Reels videosu"}</h2>
            <p>İlan kodu veya başlığıyla arayın, ardından doğru ilanı seçin.</p>
            <form onSubmit={findListings} className="reel-listing-search">
              <input value={listingSearch} onChange={(event) => setListingSearch(event.target.value)} placeholder="BRN-504 veya ilan başlığı" autoFocus />
              <button disabled={listingLoading}>Ara</button>
            </form>
            <div className="reel-listing-picker">
              {listingLoading ? <p>İlanlar yükleniyor…</p> : listings.length ? listings.map((listing) => (
                <button key={listing.ilan_no} disabled={saving} onClick={() => saveMatch(listing.ilan_no)}>
                  <strong>{listing.kisa_kod || listing.ilan_no}</strong>
                  <span>{listing.baslik || "Başlıksız ilan"}</span>
                  <small>{money(listing.fiyat)}</small>
                </button>
              )) : <p>İlan bulunamadı.</p>}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
